import { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { http, formatErr } from "../../lib/api";
import { useBrand } from "../../contexts/BrandContext";
import { PasswordField } from "../../components/PasswordField";
import { toast } from "sonner";
import { KeyRound, Loader2, ArrowLeft } from "lucide-react";

/**
 * Imposta una nuova password tramite link temporaneo monouso.
 * Il token arriva in query string. Gestisce token mancante/scaduto/già usato
 * via messaggi del backend. Non modifica ruolo o permessi.
 */
export default function PlatformResetPassword() {
  const { brand } = useBrand();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const [pwNew, setPwNew] = useState("");
  const [pwConfirm, setPwConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!token) return toast.error("Link non valido: token mancante.");
    if (pwNew.length < 12) return toast.error("La nuova password deve essere di almeno 12 caratteri.");
    if (pwNew !== pwConfirm) return toast.error("Le password non coincidono.");
    setBusy(true);
    try {
      await http.post("/auth/reset-password", { token, new_password: pwNew });
      toast.success("Password reimpostata. Effettua il login.");
      nav("/platform/login", { replace: true });
    } catch (err) {
      toast.error(formatErr(err));
    }
    setBusy(false);
  };

  return (
    <div data-testid="platform-reset-password-page" className="min-h-screen flex items-center justify-center p-4"
      style={{ background: "linear-gradient(135deg, var(--brand-dark) 0%, #2d1526 100%)", color: "white" }}>
      <form onSubmit={submit} className="w-full max-w-md p-8 rounded-2xl border border-white/10"
        style={{ background: "rgba(255,255,255,0.04)" }}>
        <h1 className="font-serif text-2xl mb-1">{brand.display_name}</h1>
        <p className="text-xs text-white/60 mb-6">Imposta una nuova password</p>

        {!token && (
          <p data-testid="reset-missing-token" className="text-sm text-rose-300 mb-4">
            Link non valido o incompleto. Richiedi un nuovo reset.
          </p>
        )}

        <label className="block mb-4">
          <span className="text-xs uppercase tracking-widest text-white/60 mb-2 block">Nuova password (min 12 char)</span>
          <PasswordField required value={pwNew} onChange={e => setPwNew(e.target.value)}
            autoComplete="new-password" data-testid="reset-new" iconClassName="text-white/70"
            className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/20 focus:outline-none" />
        </label>
        <label className="block mb-6">
          <span className="text-xs uppercase tracking-widest text-white/60 mb-2 block">Conferma nuova password</span>
          <PasswordField required value={pwConfirm} onChange={e => setPwConfirm(e.target.value)}
            autoComplete="new-password" data-testid="reset-confirm" iconClassName="text-white/70"
            className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/20 focus:outline-none" />
        </label>
        <button type="submit" disabled={busy || !token} data-testid="reset-submit"
          className="w-full py-3 rounded-lg disabled:opacity-50 uppercase tracking-widest text-sm flex items-center justify-center gap-2"
          style={{ background: "var(--brand-primary)", color: "white" }}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
          Reimposta password
        </button>
        <Link to="/platform/login" className="inline-flex items-center gap-1 mt-5 text-xs underline text-white/70 hover:text-white"
          data-testid="reset-back-login">
          <ArrowLeft className="w-3 h-3" /> Torna al login
        </Link>
      </form>
    </div>
  );
}
